import { AnalyticsService } from "@/gen/ts/v1/analytics/analytics_service_pb";
import { CollectionService } from "@/gen/ts/v1/collection/collection_service_pb";
import { EventService } from "@/gen/ts/v1/event/event_service_pb";
import { ImageService } from "@/gen/ts/v1/image/image_service_pb";
import { OrgService } from "@/gen/ts/v1/org/org_service_pb";
import { PlatformAdminService } from "@/gen/ts/v1/platform_admin/platform_admin_service_pb";
import { UserService } from "@/gen/ts/v1/user/user_service_pb";
import { ConnectError, createClient, Interceptor } from "@connectrpc/connect";
import { createGrpcWebTransport } from "@connectrpc/connect-web";
import { isServer } from "@tanstack/react-query";
import { track } from "@workspace/analytics/track";
import { getAuth } from "firebase/auth";
import { CORRELATION_HEADERS, STORAGE_KEYS } from "./observability/types";

// Helper function to get the base URL
export const getClientBaseURL = () => {
  // Server-side
  if (isServer && typeof window === "undefined") {
    console.warn("getClientBaseURL is not supported on server");
    return "";
  }
  // Client-side - will be rewritten by middleware
  return process.env.NEXT_PUBLIC_GRPC_URL || "http://localhost:8081";
};

// Auth interceptor
const authInterceptor: Interceptor = (next) => async (req) => {
  // Check if we're in a browser environment
  if (isServer && typeof window === "undefined") {
    throw new Error("Not implemented");
  }

  if (req.header.has("Authorization")) {
    return await next(req);
  }

  try {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      req.header.set("Authorization", `Bearer ${token}`);
    }
  } catch {
    // Users can be anonymous
  }

  return await next(req);
};

// CORS interceptor
const corsInterceptor: Interceptor = (next) => async (req) => {
  if (isServer) return next(req);

  req.header.set("Access-Control-Allow-Origin", "*");
  req.header.set("Access-Control-Allow-Methods", "GET,POST");
  req.header.set(
    "Access-Control-Allow-Headers",
    "Content-Type, Connect-Protocol-Version, Connect-Timeout-Ms, X-User-Agent",
  );

  return next(req);
};

// Logger interceptor
// Disable verbose client request logging to reduce console noise
const loggerInterceptor: Interceptor = (next) => async (req) => next(req);

// Correlation interceptor - automatically adds correlation IDs to requests
const correlationInterceptor: Interceptor = (next) => async (req) => {
  if (isServer) return next(req);

  try {
    // Get correlation ID from localStorage (persistent)
    const correlationId =
      typeof window !== "undefined"
        ? localStorage.getItem(STORAGE_KEYS.CORRELATION_ID)
        : null;

    // Get session ID from sessionStorage (per session)
    const sessionId =
      typeof window !== "undefined"
        ? sessionStorage.getItem(STORAGE_KEYS.SESSION_ID)
        : null;

    // Add correlation headers if available
    if (correlationId) {
      req.header.set(CORRELATION_HEADERS.CORRELATION_ID, correlationId);
    }

    if (sessionId) {
      req.header.set(CORRELATION_HEADERS.SESSION_ID, sessionId);
    }

    // Add user context if available from provider
    if (typeof window !== "undefined") {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (user?.uid) {
          req.header.set(CORRELATION_HEADERS.USER_ID, user.uid);
        }
      } catch (error) {
        // Don't fail the request if we can't get user context
        console.warn("Failed to get user context for correlation:", error);
      }
    }
  } catch (error) {
    // Don't fail the request if correlation setup fails
    console.warn("Failed to add correlation headers:", error);
  }

  return await next(req);
};

// Error interceptor: capture ConnectError details and correlation for triage
const connectErrorInterceptor: Interceptor = (next) => async (req) => {
  try {
    return await next(req);
  } catch (err: unknown) {
    let code = "UNKNOWN";
    let message = String(err);
    let requestId: string | undefined;

    if (err instanceof ConnectError) {
      code = String(err.code);
      message = err.message;
      try {
        // In connect-web, metadata is a Headers object
        const md: Headers | undefined = (err as any).metadata;
        if (md && typeof md.get === "function") {
          requestId =
            md.get("x-request-id") ?? md.get("X-Request-Id") ?? undefined;
        }
      } catch {}
    }

    try {
      const correlationId =
        typeof window !== "undefined"
          ? localStorage.getItem(STORAGE_KEYS.CORRELATION_ID) || undefined
          : undefined;
      const sessionId =
        typeof window !== "undefined"
          ? sessionStorage.getItem(STORAGE_KEYS.SESSION_ID) || undefined
          : undefined;

      track("rpc.error", {
        url: req.url,
        procedure: req.method.name,
        code,
        message,
        requestId,
        correlationId,
        sessionId,
        env: process.env.NODE_ENV,
        version: process.env.NEXT_PUBLIC_APP_VERSION,
      });
    } catch {
      // Never disrupt the user flow on analytics failure
    }

    throw err;
  }
};

// Create a transport for the Connect client (client-side)
export const clientTransport = createGrpcWebTransport({
  baseUrl: getClientBaseURL(),
  interceptors: [
    authInterceptor,
    corsInterceptor,
    // loggerInterceptor, // disabled to reduce console noise
    correlationInterceptor,
    connectErrorInterceptor,
  ],
  defaultTimeoutMs: 30000, // 30 second timeout for all requests
});

// Client-side clients
export const orgClient = createClient(OrgService, clientTransport);
export const userClient = createClient(UserService, clientTransport);
export const eventClient = createClient(EventService, clientTransport);
export const imageClient = createClient(ImageService, clientTransport);
export const analyticsClient = createClient(AnalyticsService, clientTransport);
export const platformAdminClient = createClient(
  PlatformAdminService,
  clientTransport,
);
export const collectionClient = createClient(
  CollectionService,
  clientTransport,
);
